/**
 * Comp-off requests, richer leave cancellation and amendment of approved leave — enums and schemas
 * (ADR 0031 addendum, Phase 4b).
 *
 * WHAT IS DECIDED HERE and every implementation must agree with: the lifecycle of three records, the
 * body of the extended cancel route, and the body of the amend route. Each record is decided through
 * `@hrms/workflow` (entity types `comp_off_request`, `leave_cancellation`, `leave_amendment`); none
 * of the three flips a status by hand.
 *
 *   - COMP-OFF REQUEST: an employee asks to be credited a day (or half day) worked on a holiday or
 *     weekly off. Approval creates the `CompOffCredit`; until then nothing exists to spend.
 *   - CANCELLATION: all or part of a leave request given back. The employee cancelling a WHOLE,
 *     not-yet-started request stays the direct path it always was; anything else is a
 *     `LeaveCancellationRequest`, either routed to approval (`requiresApproval`) or, for a manager
 *     or HR acting on the person's behalf, recorded as already approved.
 *   - AMENDMENT: approved leave moved to other dates. The request is untouched until the approval
 *     lands; the amendment row holds the dates before and after.
 *
 * NOT DECIDED HERE: any arithmetic. Day counts are decimal STRINGS the server computes with the same
 * rules as a new request (holidays, weekly offs, sandwich, sessions in the person's location
 * timezone); this file never derives one. Clients never send a day count except a comp-off request's
 * own `daysRequested`.
 *
 * RN-SAFE: zod only, no Node built-ins.
 */

import { z } from 'zod';
import {
  dateOnlySchema,
  instantSchema,
  leaveDaysSchema,
  leaveSessionSchema,
  orgIdSchema,
  uuidSchema,
} from './schemas/common.js';

/** A day figure that may be zero or negative (a delta), never an input. Same shape as
 * `dayFigureSchema` in `leave-encashment.ts`, redeclared so the two files stay independent. */
const dayFigureSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal day count');

// ---------------------------------------------------------------------------
// Shared lifecycle
// ---------------------------------------------------------------------------

/**
 * `pending` (filed, awaiting the workflow) → `approved` | `rejected` | `withdrawn`. All three are
 * final. A cancellation recorded for a manager or HR acting on someone's behalf is written
 * `approved` straight away. Only `pending` rows (and, for comp-off, `approved` ones) hold the
 * database's `active_key`, which is what stops a duplicate.
 */
export const LEAVE_CHANGE_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export const leaveChangeStatusSchema = z.enum(LEAVE_CHANGE_STATUSES);
export type LeaveChangeStatus = z.infer<typeof leaveChangeStatusSchema>;

/** Which rows a list returns. `mine`: the caller's own. `team`: the caller's reporting tree. `all`
 * needs `leave.balance:adjust` — HR's reach, whatever scope the grant carries. */
export const leaveChangeListScopeSchema = z.enum(['mine', 'team', 'all']);

const listQueryShape = {
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: leaveChangeStatusSchema.optional(),
  scope: leaveChangeListScopeSchema.default('mine'),
};

/** The approver's comment on approve or reject. */
export const decideLeaveChangeBodySchema = z
  .object({ comment: z.string().trim().max(500).optional() })
  .strict();

/** The requester's note on withdrawing a pending request. */
export const withdrawLeaveChangeBodySchema = z
  .object({ reason: z.string().trim().max(500).optional() })
  .strict();

// ---------------------------------------------------------------------------
// Comp-off requests
// ---------------------------------------------------------------------------

export const COMP_OFF_REQUEST_STATUSES = LEAVE_CHANGE_STATUSES;
export const compOffRequestStatusSchema = leaveChangeStatusSchema;
export type CompOffRequestStatus = LeaveChangeStatus;

/** The most one request may claim: a worked day. Half a day is `"0.5"`. Mirrors the table CHECK. */
export const COMP_OFF_REQUEST_MAX_DAYS = 1;

export const compOffRequestDaysSchema = leaveDaysSchema.refine(
  (value) => Number.parseFloat(value) <= COMP_OFF_REQUEST_MAX_DAYS,
  'A comp-off request covers at most one day',
);

export const compOffRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  /** The day worked, in the person's location timezone. */
  workedDate: dateOnlySchema,
  daysRequested: leaveDaysSchema,
  reason: z.string(),
  status: compOffRequestStatusSchema,
  /** The credit approval created; null until then. */
  creditId: uuidSchema.nullable(),
  /** When that credit lapses, per the comp-off leave type's validity; null until approved. */
  creditExpiresOn: dateOnlySchema.nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  currentApproverName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type CompOffRequest = z.infer<typeof compOffRequestSchema>;

export const createCompOffRequestBodySchema = z
  .object({
    /** A day that has happened, on which the person worked a holiday or weekly off — the API
     * checks both in the person's location timezone, not here. */
    workedDate: dateOnlySchema,
    daysRequested: compOffRequestDaysSchema,
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type CreateCompOffRequestBody = z.infer<typeof createCompOffRequestBodySchema>;

export const listCompOffRequestsQuerySchema = z.object({ ...listQueryShape });

// ---------------------------------------------------------------------------
// Cancellation
// ---------------------------------------------------------------------------

/** `full`: the whole request. `partial`: an inclusive date range at one end of it — a cancellation
 * that would split the request in two is refused; amend it instead. */
export const LEAVE_CANCELLATION_KINDS = ['full', 'partial'] as const;
export const leaveCancellationKindSchema = z.enum(LEAVE_CANCELLATION_KINDS);
export type LeaveCancellationKind = z.infer<typeof leaveCancellationKindSchema>;

/**
 * The body of `POST /leave/requests/:id/cancel`. BACKWARD COMPATIBLE: `{}` or `{ comment }` parses to
 * a `full` cancellation, which is what the route did before. `comment` doubles as the cancellation's
 * reason. Who is asking (the owner, a manager, HR) comes from the session, never the body.
 */
export const leaveCancellationInputSchema = z
  .object({
    comment: z.string().max(500).optional(),
    kind: leaveCancellationKindSchema.default('full'),
    /** Partial only: the first and last day being given back, both inclusive. */
    fromDate: dateOnlySchema.optional(),
    toDate: dateOnlySchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'full') {
      for (const key of ['fromDate', 'toDate'] as const) {
        if (value[key] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [key],
            message: 'A full cancellation takes no dates',
          });
        }
      }
      return;
    }
    if (value.fromDate === undefined || value.toDate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [value.fromDate === undefined ? 'fromDate' : 'toDate'],
        message: 'A partial cancellation needs fromDate and toDate',
      });
    } else if (value.toDate < value.fromDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toDate'],
        message: 'toDate must be on or after fromDate',
      });
    }
  });
export type LeaveCancellationInput = z.infer<typeof leaveCancellationInputSchema>;

export const leaveCancellationRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  leaveRequestId: uuidSchema,
  /** The leave request's display code, e.g. "SL-6729". */
  requestCode: z.string(),
  leaveTypeName: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  kind: leaveCancellationKindSchema,
  /** Partial only. */
  fromDate: dateOnlySchema.nullable(),
  toDate: dateOnlySchema.nullable(),
  /** The days given back, computed by the server at filing. */
  days: leaveDaysSchema,
  reason: z.string().nullable(),
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  /** True when someone other than the leave's owner asked. */
  onBehalf: z.boolean(),
  /** False for a manager or HR acting directly: the row is the record, already `approved`. */
  requiresApproval: z.boolean(),
  status: leaveChangeStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  currentApproverName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type LeaveCancellationRequest = z.infer<typeof leaveCancellationRequestSchema>;

export const listLeaveCancellationRequestsQuerySchema = z.object({
  ...listQueryShape,
  leaveRequestId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Amendment
// ---------------------------------------------------------------------------

/** One version of a leave request's dates, with the days it is charged. */
export const leaveDateSpanSchema = z.object({
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  startSession: leaveSessionSchema,
  endSession: leaveSessionSchema,
  days: leaveDaysSchema,
});
export type LeaveDateSpan = z.infer<typeof leaveDateSpanSchema>;

/**
 * The body of `POST /leave/requests/:id/amend`. A session left out keeps the request's current one.
 * Only the owner amends their own approved, not-hourly request; the leave type cannot change (file a
 * new request for that).
 */
export const amendLeaveRequestBodySchema = z
  .object({
    newStartDate: dateOnlySchema,
    newEndDate: dateOnlySchema,
    newStartSession: leaveSessionSchema.optional(),
    newEndSession: leaveSessionSchema.optional(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.newEndDate < value.newStartDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newEndDate'],
        message: 'newEndDate must be on or after newStartDate',
      });
    }
  });
export type AmendLeaveRequestBody = z.infer<typeof amendLeaveRequestBodySchema>;

export const leaveAmendmentSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  leaveRequestId: uuidSchema,
  requestCode: z.string(),
  leaveTypeName: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  /** The request's dates when the amendment was filed. */
  before: leaveDateSpanSchema,
  /** The dates being asked for, with the days they would be charged. */
  after: leaveDateSpanSchema,
  /** `after.days − before.days`: positive debits more from the balance, negative returns days. */
  daysDelta: dayFigureSchema,
  reason: z.string(),
  status: leaveChangeStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  currentApproverName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type LeaveAmendment = z.infer<typeof leaveAmendmentSchema>;

export const listLeaveAmendmentsQuerySchema = z.object({
  ...listQueryShape,
  leaveRequestId: uuidSchema.optional(),
});
